import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import { Mail, Lock, User as UserIcon, Loader2, Radio, Music, Mic, Headphones, GraduationCap, Check, Flame, Sparkles } from 'lucide-react';

const PERKS = [
  { icon: Headphones, title: '24/7 Radio rotation',  body: 'Your tracks air the moment you upload — no gatekeepers, no pitching.' },
  { icon: GraduationCap, title: 'Music Biz Academy', body: '7 tracks, 38 lessons, 7 specialist AI agents unlocked by learning.' },
  { icon: Radio,       title: 'Distribution Center',  body: 'Affiliate partnerships with DistroKid, TuneCore, CD Baby — no extra fees.' },
  { icon: Mic,         title: 'Public music profile', body: 'Your tracks display on your channel page with streaming player.' },
  { icon: Sparkles,    title: 'VidCreForge free baked-in', body: 'Every ReaperCast account includes a full VidCreForge creator account — video too.' },
];

export default function MusicianSignup() {
  const nav = useNavigate();
  const { signup } = useApp();
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pwd) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      await signup(email, pwd, name || undefined, { profile_type: 'musician', reapercast_optin: true });
      toast.success('Welcome to ReaperCast');
      nav('/reapercast');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="musician-signup" className="min-h-screen bg-[#0a0a0c] text-white flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-55" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,10,12,0.92), rgba(10,10,12,0.35) 55%, rgba(167,139,250,0.35))' }} />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2"><Logo /><span className="chip active text-[10px] uppercase">ReaperCast</span></Link>
          <div>
            <div className="flex items-center gap-2 text-[#a78bfa] mb-3"><Radio className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">For musicians</span></div>
            <h2 className="font-display text-[56px] leading-[0.98] max-w-[520px]">The <span className="brand-gradient">forge</span><br/>for your music career.</h2>
            <p className="text-white/80 text-[15px] mt-4 max-w-[480px]">Upload tracks, learn the business, distribute worldwide, and get AI coaching from specialists who actually know the game.</p>
            <ul className="mt-8 space-y-3 max-w-[480px]">
              {PERKS.map(p => (
                <li key={p.title} className="flex items-start gap-3 text-[13.5px]">
                  <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(167,139,250,0.18)', color: '#a78bfa' }}><p.icon className="w-4 h-4" /></div>
                  <div>
                    <div className="font-semibold">{p.title}</div>
                    <div className="text-white/65">{p.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[520px] flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[400px]">
          <div className="lg:hidden mb-6 flex items-center gap-2"><Link to="/"><Logo /></Link><span className="chip active text-[10px] uppercase">ReaperCast</span></div>
          <div className="flex items-center gap-2 text-[#a78bfa] mb-2"><Music className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Musician signup</span></div>
          <h1 className="font-display text-[32px] leading-none">Claim your ReaperCast.</h1>
          <p className="text-white/60 text-[14px] mt-2">Free forever. Includes a full VidCreForge creator account with video tools.</p>

          <form onSubmit={submit} className="mt-6 space-y-3">
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#a78bfa]/60">
              <UserIcon className="w-4 h-4 text-white/50" />
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Artist name" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="musician-name-input" />
            </label>
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#a78bfa]/60">
              <Mail className="w-4 h-4 text-white/50" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@domain.com" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="musician-email-input" required />
            </label>
            <label className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 focus-within:border-[#a78bfa]/60">
              <Lock className="w-4 h-4 text-white/50" />
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="••••••••" className="bg-transparent flex-1 py-3 focus:outline-none text-[14px] placeholder:text-white/40" data-testid="musician-password-input" required />
            </label>
            <button type="submit" disabled={loading} className="w-full py-3 rounded-full font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg,#a78bfa,#ff2e63)' }} data-testid="musician-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" />} Launch my ReaperCast
            </button>
          </form>

          <div className="text-[12.5px] text-white/45 mt-4 leading-relaxed">
            <div className="flex items-start gap-1.5"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> Free ReaperCast (up to 3 tracks in radio rotation)</div>
            <div className="flex items-start gap-1.5 mt-1"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> Free VidCreForge creator account (video hosting, monetization)</div>
            <div className="flex items-start gap-1.5 mt-1"><Check className="w-3.5 h-3.5 text-[#22d3ee] mt-0.5 shrink-0" /> Upgrade ReaperCast tier anytime for unlimited tracks and advanced academy</div>
          </div>

          <div className="text-[13px] text-white/60 mt-6 text-center">
            Already have an account? <Link to="/login" className="text-[#a78bfa] hover:underline">Sign in</Link>
            <div className="mt-1">Not a musician? <Link to="/signup" className="text-[#ff6b8a] hover:underline">Regular signup</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}
